import {useFeatureIsOn} from "@growthbook/growthbook-react";
import * as React from "react";

import type {AppFeatures} from "@khan/analytics-growthbook/app-features";

/**
 * The HOC to check if a GrowthBook feature is enabled or not.
 *
 * Examples:
 *
 * - export default withFeatureIsOn("feature_name")(WrappedComponent);
 *
 * - export default compose(
 *      withFeatureIsOn("feature_name"),
 *      withRouter
 *   )(WrappedComponent);
 *
 * NOTE: Only one feature can be used with a component.
 */
export type WithFeatureIsOn = {
    // Whether the feature is enabled or not
    featureIsOn: boolean;
};

export type WithoutFeatureIsOn<T> = Omit<T, keyof WithFeatureIsOn>;

export const withFeatureIsOn =
    (name: keyof AppFeatures) =>
    <
        C extends React.ComponentType<
            React.ComponentProps<C> & WithFeatureIsOn
        > & {
            displayName?: string;
        },
    >(
        WrappedComponent: C,
    ) => {
        type OuterProps = WithoutFeatureIsOn<
            JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>
        >;

        // Try to create a nice displayName for React Dev Tools.
        const displayName =
            WrappedComponent.displayName ||
            WrappedComponent.name ||
            "Component";

        // Creating the inner component. The calculated Props type here is the where the magic happens.
        const ComponentWithFeature = (props: OuterProps) => {
            const featureIsOn = useFeatureIsOn<AppFeatures>(name);

            return (
                <WrappedComponent
                    {...(props as any)}
                    featureIsOn={featureIsOn}
                />
            );
        };

        ComponentWithFeature.displayName = `withFeatureIsOn({name:"${name}"})(${displayName})`;

        return ComponentWithFeature;
    };
